import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, List, X } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import { ZR_BANDS, ZR_UI } from "../i18n/zwischenraum";

const ZwischenraumReader = () => {
  const { band: slug } = useParams();
  const { lang } = useLang();
  const ui = ZR_UI[lang] || ZR_UI.de;
  const b = ZR_BANDS.find((x) => x.slug === slug);

  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let alive = true;
    fetch(`/ebooks/${slug}.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(false));
    return () => { alive = false; };
  }, [slug]);

  const chapters = useMemo(() => data?.chapters || [], [data]);
  const chapterTitle = (c, i) => c.title || (i === 0 ? ui.opening : `${ui.chapters} ${i}`);

  const goto = (n) => {
    setIdx(n);
    setNavOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!b) return <Navigate to="/library/zwischenraum" replace />;

  const cur = chapters[idx];

  return (
    <div data-testid={`zr-reader-${b.slug}`} className="min-h-screen bg-obsidian pt-20 md:pt-24">
      <Seo pageKey="library" path={`/library/zwischenraum/${b.slug}/lesen`} title={b.title} description={b.subtitle} />

      {/* control bar (below global navbar) */}
      <div className="border-b border-white/10 bg-obsidian/95">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-4 md:px-10">
          <Link to={`/library/zwischenraum/${b.slug}`} data-testid="reader-back"
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/50 transition-colors hover:text-champagne">
            <ArrowLeft size={14} /> {ui.backToBand}
          </Link>
          <span className="hidden truncate font-serif text-sm italic text-white/60 md:block">{b.title}</span>
          <div className="flex items-center gap-4">
            <a href={`/ebooks/${b.slug}.pdf`} download data-testid="reader-download"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-champagne transition-colors hover:text-champagne-light">
              <Download size={14} /> <span className="hidden sm:inline">{ui.download}</span>
            </a>
            <button onClick={() => setNavOpen((v) => !v)} data-testid="reader-toc-toggle"
              className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-champagne lg:hidden">
              {navOpen ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-[1400px] gap-10 px-5 py-12 md:px-10 md:py-16">
        {/* chapter nav */}
        <aside className={`${navOpen ? "block" : "hidden"} fixed inset-0 z-40 overflow-y-auto bg-obsidian px-6 py-24 lg:static lg:z-0 lg:block lg:w-72 lg:shrink-0 lg:bg-transparent lg:px-0 lg:py-0`}
          data-testid="reader-toc">
          <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-champagne">{ui.chapters}</p>
          <nav className="space-y-1 lg:sticky lg:top-24">
            {chapters.map((c, i) => (
              <button key={i} onClick={() => goto(i)} data-testid={`reader-chapter-${i}`}
                className={`block w-full border-l-2 py-2 pl-4 text-left text-[13.5px] font-light leading-snug transition-colors ${
                  i === idx ? "border-champagne text-champagne" : "border-white/10 text-white/55 hover:border-white/30 hover:text-white/80"
                }`}>
                {chapterTitle(c, i)}
              </button>
            ))}
          </nav>
        </aside>

        {/* content */}
        <main className="min-w-0 flex-1">
          {data === null && <p className="text-white/40" data-testid="reader-loading">{ui.loading}</p>}
          {cur && (
            <article className="mx-auto max-w-2xl" data-testid="reader-content">
              {(cur.title || idx === 0) && (
                <h1 className="mb-10 font-serif text-3xl font-light leading-tight text-white md:text-4xl">
                  {chapterTitle(cur, idx)}
                </h1>
              )}
              {cur.items.map((it, k) =>
                it.type === "h2" ? (
                  <h2 key={k} className="mb-4 mt-10 font-serif text-xl font-light italic text-champagne/85"
                    dangerouslySetInnerHTML={{ __html: it.html }} />
                ) : (
                  <p key={k} className="mb-6 font-serif text-[19px] leading-[1.75] text-white/80"
                    dangerouslySetInnerHTML={{ __html: it.html }} />
                )
              )}

              <div className="mt-16 flex items-center justify-between border-t border-white/10 pt-8">
                <button onClick={() => goto(idx - 1)} disabled={idx === 0} data-testid="reader-prev"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors enabled:hover:text-champagne disabled:opacity-30">
                  <ArrowLeft size={14} /> {ui.prev}
                </button>
                <span className="text-[11px] tracking-[0.2em] text-white/30">{idx + 1} / {chapters.length}</span>
                <button onClick={() => goto(idx + 1)} disabled={idx >= chapters.length - 1} data-testid="reader-next"
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-white/60 transition-colors enabled:hover:text-champagne disabled:opacity-30">
                  {ui.next} <ArrowRight size={14} />
                </button>
              </div>
            </article>
          )}
        </main>
      </div>
    </div>
  );
};

export default ZwischenraumReader;
